import { BoxLink } from "@/components/ui/Link";
import { Center, Flex, Heading, Text } from "@givenwell/components";
import { ChevronLeftIcon, ChevronRightIcon } from "@givenwell/core-solid-icons";
import { useDebouncedCallback } from "@givenwell/hooks";
import { styled } from "@givenwell/stitches";
import { colors, fonts } from "@givenwell/theme";
import { Link, LinkOptions } from "@tanstack/react-router";
import { ReactNode, useEffect, useRef, useState } from "react";

export type ShelfProps = {
  title?: ReactNode;
  children?: ReactNode;
  enableSeeMore?: boolean;
  seeMoreLinkOptions?: LinkOptions;
  onSeeMore?: () => void;
  size?: "big";
};
export function Shelf({ title, children, size, enableSeeMore, seeMoreLinkOptions, onSeeMore }: ShelfProps) {
  const scrollBox = useRef<HTMLDivElement>(null);
  const [isOnLeftEdge, setIsOnLeftEdge] = useState(true);
  const [isOnRightEdge, setIsOnRightEdge] = useState(false);

  const handleScroll = useDebouncedCallback(() => {
    const scrollLeft = scrollBox.current?.scrollLeft ?? 0;
    const scrollWidth = scrollBox.current?.scrollWidth ?? 0;
    const clientWidth = scrollBox.current?.clientWidth ?? 0;
    setIsOnLeftEdge(scrollLeft < 8);
    setIsOnRightEdge(scrollLeft + clientWidth >= scrollWidth - 8);
  }, 80);

  useEffect(() => {
    handleScroll();
  }, [handleScroll]);

  function shiftImages(delta: number) {
    const container = scrollBox.current;
    if (!container) return;

    const columns = parseFloat(getComputedStyle(container).getPropertyValue("--grid-columns"));
    const truncatedColumns = Math.floor(columns);

    const itemSize = container.firstElementChild?.getBoundingClientRect().width || 200;
    const gap = parseFloat(getComputedStyle(container).getPropertyValue("column-gap"));

    const scrollLeft = container.scrollLeft;
    const nextScrollLeft = scrollLeft + delta * truncatedColumns * (itemSize + gap);
    container.scrollTo({ left: nextScrollLeft, behavior: "smooth" });
  }

  let titleComponent = (
    <Heading
      css={{
        fontScale: "md",
        fontFamily: fonts.ftHabit,
        "@sm": { fontSize: 20, lineHeight: "24px" },
        display: "flex",
        items: "center",
        spaceX: 2,
        flex: "1 0 0px",
      }}
    >
      <span>{title}</span>
      {enableSeeMore && <ChevronRightIcon color={colors.text2} />}
    </Heading>
  );

  if (enableSeeMore) {
    titleComponent = (
      <Link {...seeMoreLinkOptions} onClick={onSeeMore} style={{ flex: "1 0 0px" }}>
        {titleComponent}
      </Link>
    );
  }

  return (
    <Flex css={{ flexDir: "column" }}>
      <Flex css={{ h: 36, items: "center", mb: 8, mt: 0 }}>
        {titleComponent}
        {(!isOnLeftEdge || !isOnRightEdge) && (
          <ScrollyButtonGroup>
            <ScrollyButton
              // css={{ ml: 8 }}
              aria-label="Scroll left"
              disabled={isOnLeftEdge}
              onClick={() => shiftImages(-1)}
            >
              <ChevronLeftIcon />
            </ScrollyButton>
            <ScrollyButton
              // css={{ ml: 8 }}
              aria-label="Scroll right"
              disabled={isOnRightEdge}
              onClick={() => shiftImages(1)}
            >
              <ChevronRightIcon />
            </ScrollyButton>
          </ScrollyButtonGroup>
        )}
      </Flex>
      <ShelfScrollContainer size={size} ref={scrollBox} onScroll={handleScroll}>
        {children}
        {enableSeeMore && <ShelfSeeMoreButton linkOptions={seeMoreLinkOptions} onClick={onSeeMore} />}
      </ShelfScrollContainer>
    </Flex>
  );
}

function ShelfSeeMoreButton({ linkOptions, onClick }: { linkOptions?: LinkOptions; onClick?: () => void }) {
  return (
    <BoxLink {...linkOptions} onClick={onClick}>
      <Center
        css={{
          width: "100%",
          aspectRatio: "16/9",
          fontScale: "sm",
          fontWeight: 500,
          flexDirection: "column",
        }}
      >
        <Center css={{ size: 44, borderRadius: 9999, bg: colors.warmGrey, color: colors.text2, position: "relative" }}>
          <ChevronRightIcon />
          <Text
            css={{
              fontScale: "sm",
              lineHeight: 1,
              color: colors.text,
              position: "absolute",
              width: "max-content",
              top: 44 + 6,
              fontFamily: fonts.ftHabit,
            }}
          >
            See more
          </Text>
        </Center>
      </Center>
    </BoxLink>
  );
}

const ShelfScrollContainer = styled("div", {
  scrollbarWidth: "none",
  MsOverflowStyle: "none",
  WebkitOverflowScrolling: "touch",
  overflowX: "scroll",
  overflowY: "hidden",

  display: "grid",
  gridTemplateRows: "1 max-content",
  gridAutoFlow: "column",

  "--container-width": "calc(100%)",
  "--grid-padding": "var(--layout-margin)",
  "--grid-gap": "8px",
  "--grid-columns": 2,
  "--grid-item-width":
    "calc((var(--container-width) - (var(--grid-columns) - 1) * var(--grid-gap)) / var(--grid-columns))",

  "@md": {
    "--grid-gap": "16px",
  },
  "@lg": {
    mask: "linear-gradient(90deg, transparent calc(var(--grid-padding) - var(--grid-gap)), #000 16px, #000 calc(100% - var(--grid-padding)), transparent calc(100% - (var(--grid-padding) - var(--grid-gap))))",
  },

  mx: "calc(-1 * var(--grid-padding))",
  px: "var(--grid-padding)",
  gap: "var(--grid-gap)",
  gridAutoColumns: "var(--grid-item-width)",

  pb: 16,

  variants: {
    size: {
      medium: {
        "--grid-columns": 2,
        "@xs": {
          "--grid-columns": 1.5,
        },
        "@sm": {
          "--grid-columns": 3,
        },
        "@md": {
          "--grid-columns": 4,
        },
        "@lg": {
          "--grid-columns": 4,
        },
        "@xl": {
          "--grid-columns": 5,
        },
      },
      big: {
        "--grid-columns": 1.5,
        "@xs": {
          "--grid-columns": 1,
        },
        "@sm": {
          "--grid-columns": 2,
        },
        "@md": {
          "--grid-columns": 3,
        },
        "@lg": {
          "--grid-columns": 4,
        },
        "@xl": {
          "--grid-columns": 4,
        },
      },
    },
  },
  defaultVariants: {
    size: "medium",
  },
});

const ScrollyButtonGroup = styled("div", {
  height: 36,
  borderRadius: 9999,
  backgroundColor: colors.warmGreyHalf,
  display: "flex",
});

const ScrollyButton = styled("button", {
  size: 36,
  items: "center",
  justify: "center",
  borderRadius: 9999,
  // backgroundColor: colors.warmGrey,
  color: colors.navy,
  "&[disabled]": {
    color: colors.text3,
  },

  display: "none",
  "@media (pointer: fine)": {
    display: "flex",
  },
});
